import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { IncidentKanbanBoard } from '@/features/incident-kanban'
import { IncidentFilterBar, IncidentListTable, incidentSearchSchema, toBoardParams, isNumberSearch, type IncidentFilters } from '@/features/incident-list'
import { incidentApi } from '@/entities/incident'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'

export const Route = createFileRoute('/incidents/')({
  component: IncidentsPage,
  staticData: { authorize: true },
  validateSearch: incidentSearchSchema,
})

function IncidentsPage() {
  const filters = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const view = filters.view ?? 'board'

  const setFilters = (next: IncidentFilters) => {
    const switchToList = view === 'board' && !!next.q && next.q !== filters.q
    navigate({ search: { ...next, view: switchToList ? 'list' : next.view ?? view }, replace: true })
  }

  const switchView = (next: 'board' | 'list') =>
    navigate({ search: { ...filters, view: next, q: next === 'board' ? undefined : filters.q, page: undefined } })

  const openExactMatch = async (q: string) => {
    if (!isNumberSearch(q)) return
    const result = await incidentApi.list({ search: q, pageSize: 2 }, { skipErrorToast: true }).catch(() => null)
    if (result?.items.length === 1) {
      navigate({ to: '/incidents/$incidentId', params: { incidentId: String(result.items[0].id) } })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-4 px-4 pt-4 pb-2">
        <PageHeader title="Incidents" description="Triage, search and report on incidents." breadcrumbs={[{ label: 'Incidents' }]} />
        <Tabs value={view} onValueChange={(v) => switchView(v as 'board' | 'list')}>
          <TabsList>
            <TabsTrigger value="board">Board</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="px-4 pb-3">
        <IncidentFilterBar value={filters} onChange={setFilters} mode={view} onSearchSubmit={openExactMatch} />
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {view === 'board' ? (
          <IncidentKanbanBoard
            params={toBoardParams(filters)}
            onShowInList={(status) => navigate({ search: { ...filters, view: 'list', status, range: 'all', page: undefined } })}
          />
        ) : (
          <div className="px-4 pb-4">
            <IncidentListTable filters={filters} onFiltersChange={setFilters} />
          </div>
        )}
      </div>
    </div>
  )
}
